import { DateTime } from "luxon";
import { useState } from "react";
import { Controller, FieldValues, Path, useFormContext } from "react-hook-form";
import { SxProps } from "@mui/system";
import { Theme } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EventIcon from "@mui/icons-material/Event";
import ClearIcon from "@mui/icons-material/Clear";
import { DateWithoutTime } from "../model/DateWithoutTime";

interface DateInputProps<T extends FieldValues> {
  source: Path<T>;
  allowPastDates?: boolean;
  sx?: SxProps<Theme>;
  label?: string;
  size?: "small";
  helperText?: string;
  showDaySteppers?: boolean;
  showClearButton?: boolean;
  disabled?: boolean;
  required?: boolean;
  validate?: (value: DateWithoutTime | null) => string | true;
}

/**
 * An input component for `DateWithoutTime` values.
 */
export function DateInput2<T extends FieldValues>(props: DateInputProps<T>) {
  const {
    source,
    allowPastDates = false,
    sx,
    label = "Datum",
    size,
    helperText,
    showDaySteppers = false,
    disabled = false,
    showClearButton = false,
    required = true,
    validate,
  } = props;
  const { control } = useFormContext<T>();
  const [isOpen, setIsOpen] = useState(false);
  const iconSize = size === "small" ? "small" : "medium";

  const defaultValidate = (value: DateWithoutTime | null) => {
    if (!value && required) {
      return "Bitte wähle ein Datum aus.";
    }
    if (value && value < DateWithoutTime.today() && !allowPastDates) {
      return "Bitte wählen ein Datum in der Zukunft aus.";
    }
    return true;
  };

  return (
    <Controller
      name={source}
      control={control}
      render={({ field, formState }) => {
        const error = formState.errors[source]?.message;
        const errorMessage = typeof error === "string" ? error : undefined;
        const rawValue: unknown = field.value;

        const pickerValue: DateTime | null =
          rawValue instanceof DateTime
            ? rawValue
            : rawValue instanceof DateWithoutTime
              ? rawValue.toDateTime()
              : typeof rawValue === "string"
                ? DateTime.fromISO(rawValue)
                : null;

        const handleStep = (days: number) => {
          const currentDate = pickerValue ?? DateTime.now();
          const nextDate = currentDate.plus({ days });
          const nextValue = rawValue instanceof DateWithoutTime ? DateWithoutTime.fromDateTime(nextDate) : nextDate;
          field.onChange({ target: { value: nextValue } });
        };

        return (
          <DatePicker
            sx={sx}
            label={label}
            inputRef={field.ref}
            value={pickerValue}
            disabled={disabled || formState.isSubmitting}
            open={isOpen}
            onOpen={() => {
              setIsOpen(true);
            }}
            onClose={() => {
              setIsOpen(false);
            }}
            slotProps={{
              textField: {
                required,
                size,
                onBlur: field.onBlur,
                InputProps: {
                  endAdornment: (
                    <InputAdornment position="end" sx={{ display: "flex", gap: 0.25 }}>
                      {showDaySteppers && (
                        <>
                          <IconButton
                            size={iconSize}
                            onClick={() => handleStep(-1)}
                            disabled={disabled || formState.isSubmitting}
                            aria-label="Vorheriger Tag"
                            edge="end"
                          >
                            <RemoveCircleOutlineIcon fontSize={iconSize} />
                          </IconButton>
                          <IconButton
                            size={iconSize}
                            onClick={() => setIsOpen(true)}
                            disabled={disabled || formState.isSubmitting}
                            aria-label="Kalender öffnen"
                            edge="end"
                          >
                            <EventIcon fontSize={iconSize} />
                          </IconButton>
                          <IconButton
                            size={iconSize}
                            onClick={() => handleStep(1)}
                            disabled={disabled || formState.isSubmitting}
                            aria-label="Nächster Tag"
                            edge="end"
                          >
                            <AddCircleOutlineIcon fontSize={iconSize} />
                          </IconButton>
                        </>
                      )}
                      {!showDaySteppers && (
                        <IconButton
                          size={iconSize}
                          onClick={() => setIsOpen(true)}
                          disabled={disabled || formState.isSubmitting}
                          aria-label="Kalender öffnen"
                          edge="end"
                        >
                          <EventIcon fontSize={iconSize} />
                        </IconButton>
                      )}
                      {field.value && showClearButton && (
                        <IconButton
                          size={iconSize}
                          onClick={() => field.onChange({ target: { value: null } })}
                          disabled={disabled || formState.isSubmitting}
                          aria-label="Datum löschen"
                          edge="end"
                        >
                          <ClearIcon fontSize={iconSize} />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                },
                ...(errorMessage ? { error: true, helperText: errorMessage } : { helperText }),
              },
            }}
            onChange={(value) => {
              field.onChange({
                target: { value: value && value.isValid ? DateWithoutTime.fromDateTime(value) : null },
              });
            }}
          />
        );
      }}
      rules={{ validate: validate ?? defaultValidate }}
    />
  );
}
